import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import articlePromotionImpactService, { ArticlePromotionPair } from '../services/articlePromotionImpactService';
import promotionsService, { Promotion as ApiPromotion } from '../services/promotionsService';
import { exportToCSV, formatNumberForCSV, formatDateForCSV } from '../utils/csvExport';

interface Promotion extends Omit<ApiPromotion, 'is_active'> {
  status: 'en_cours' | 'termine' | 'en_attente' | 'annule';
}

const ArticlePromotionView: React.FC = () => {
  const { activeDatabase } = useDatabaseContext();
  const [pairs, setPairs] = useState<ArticlePromotionPair[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPromotionId, setSelectedPromotionId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingImpact, setEditingImpact] = useState<{ pairId: string; value: string } | null>(null);
  const [savingImpact, setSavingImpact] = useState<Record<string, boolean>>({});

  // Load promotions
  const loadPromotions = useCallback(async () => {
    if (!activeDatabase?.id) return;
    
    try {
      const data = await promotionsService.getPromotions(activeDatabase.id);
      const uiPromotions: Promotion[] = data.map(apiPromotion => ({
        ...apiPromotion,
        status: computePromotionStatus(apiPromotion)
      }));
      setPromotions(uiPromotions);
    } catch (error) {
      console.error('Error loading promotions:', error);
    }
  }, [activeDatabase?.id]);

  const computePromotionStatus = (promotion: ApiPromotion): 'en_cours' | 'termine' | 'en_attente' | 'annule' => {
    if (!promotion.is_active) return 'annule';
    
    const now = new Date();
    const startDate = new Date(promotion.start_date);
    const endDate = new Date(promotion.end_date);
    
    if (now < startDate) return 'en_attente';
    if (now > endDate) return 'termine';
    return 'en_cours';
  };

  // Load article-promotion pairs
  const loadPairs = useCallback(async () => {
    if (!activeDatabase?.id) return;
    
    try {
      setLoading(true);
      const data = await articlePromotionImpactService.getArticlePromotionPairs(
        activeDatabase.id,
        selectedPromotionId || undefined
      );
      setPairs(data);
    } catch (error) {
      console.error('Error loading article-promotion pairs:', error);
    } finally {
      setLoading(false);
    }
  }, [activeDatabase?.id, selectedPromotionId]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  useEffect(() => {
    loadPairs();
  }, [loadPairs]);

  // Handle impact percentage edit
  const handleImpactEdit = (pair: ArticlePromotionPair) => {
    setEditingImpact({
      pairId: pair.id,
      value: pair.impact_percentage.toString()
    });
  };

  // Validate impact percentage
  const validateImpactPercentage = (value: string): boolean => {
    const num = parseFloat(value);
    return !isNaN(num) && num >= -100 && num <= 500;
  };

  // Save impact percentage
  const handleImpactSave = async (pair: ArticlePromotionPair, value: string) => {
    if (!activeDatabase?.id) return;
    
    const numValue = parseFloat(value);
    if (!validateImpactPercentage(value)) {
      alert('Le pourcentage d\'impact doit être entre -100% et 500%');
      setEditingImpact(null);
      return;
    }

    try {
      setSavingImpact(prev => ({ ...prev, [pair.id]: true }));
      
      await articlePromotionImpactService.upsertImpact(
        activeDatabase.id,
        pair.promotion_id,
        pair.article_id,
        numValue
      );
      
      // Reload pairs to get updated metrics
      await loadPairs();
      setEditingImpact(null);
    } catch (error) {
      console.error('Error saving impact:', error);
      alert('Erreur lors de la sauvegarde de l\'impact');
    } finally {
      setSavingImpact(prev => ({ ...prev, [pair.id]: false }));
    }
  };

  // Handle impact input change
  const handleImpactChange = (value: string) => {
    if (editingImpact) {
      setEditingImpact({ ...editingImpact, value });
    }
  };

  // Handle impact blur/enter
  const handleImpactBlur = (pair: ArticlePromotionPair) => {
    if (editingImpact && editingImpact.pairId === pair.id) {
      handleImpactSave(pair, editingImpact.value);
    }
  };

  const handleImpactKeyDown = (e: React.KeyboardEvent, pair: ArticlePromotionPair) => {
    if (e.key === 'Enter' && editingImpact && editingImpact.pairId === pair.id) {
      handleImpactSave(pair, editingImpact.value);
    } else if (e.key === 'Escape') {
      setEditingImpact(null);
    }
  };

  // Filter pairs by search term
  const filteredPairs = useMemo(() => {
    return pairs.filter(pair => {
      const matchesSearch = 
        pair.promotion_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pair.article_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pair.article?.nom && pair.article.nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (pair.article?.ref && pair.article.ref.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  }, [pairs, searchTerm]);

  // Get article display name
  const getArticleDisplayName = (pair: ArticlePromotionPair): string => {
    return pair.article?.nom || pair.article?.ref || pair.article_id || 'N/A';
  };

  // Get article ref
  const getArticleRef = (pair: ArticlePromotionPair): string => {
    return pair.article?.ref || pair.article_id || 'N/A';
  };

  // Format number
  const formatNumber = (num: number): string => {
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // Get status label in French
  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'en_cours': 'En cours',
      'en_attente': 'En attente',
      'termine': 'Terminé',
      'annule': 'Annulé'
    };
    return statusMap[status] || status;
  };

  // Handle CSV export
  const handleExportCSV = useCallback(() => {
    if (filteredPairs.length === 0) {
      return;
    }

    // Prepare CSV data
    const csvData = filteredPairs.map(pair => ({
      'Référence article': getArticleRef(pair),
      'Nom article': getArticleDisplayName(pair),
      'Promotion': pair.promotion_name,
      'Date début': formatDateForCSV(pair.promotion_start_date),
      'Date fin': formatDateForCSV(pair.promotion_end_date),
      'Statut': getStatusLabel(pair.promotion_status),
      'Impact (%)': formatNumberForCSV(pair.impact_percentage),
      'Ventes prédites': formatNumberForCSV(pair.predicted_sales_sum),
      'Ventes additionnelles': formatNumberForCSV(pair.additional_sales),
      'Ventes historiques': formatNumberForCSV(pair.historical_sales_sum),
    }));

    // Define headers
    const headers = [
      'Référence article',
      'Nom article',
      'Promotion',
      'Date début',
      'Date fin',
      'Statut',
      'Impact (%)',
      'Ventes prédites',
      'Ventes additionnelles',
      'Ventes historiques',
    ];

    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD
    const filename = `article-promotion-export-${dateStr}`;

    // Export to CSV
    exportToCSV(csvData, filename, headers);
  }, [filteredPairs]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par promotion, article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Promotion Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedPromotionId || ''}
              onChange={(e) => setSelectedPromotionId(e.target.value ? parseInt(e.target.value) : null)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Toutes les promotions</option>
              {promotions.map(promotion => (
                <option key={promotion.id} value={promotion.id}>
                  {promotion.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export Button */}
          <div className="flex items-center">
            <button
              onClick={handleExportCSV}
              disabled={filteredPairs.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              title="Exporter en CSV"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Exporter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-gray-400">Chargement...</div>
          </div>
        ) : filteredPairs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-4">
              <FunnelIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat</h3>
            <p className="text-gray-500">
              {searchTerm || selectedPromotionId
                ? 'Aucun article-promotion ne correspond à vos critères.'
                : 'Aucun article-promotion disponible.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Article
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Promotion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Impact (%)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ventes prédites
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ventes additionnelles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ventes historiques
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPairs.map((pair) => {
                  const isEditing = editingImpact?.pairId === pair.id;
                  const isSaving = savingImpact[pair.id];
                  
                  return (
                    <tr key={pair.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getArticleDisplayName(pair)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getArticleRef(pair)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {pair.promotion_name}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                          pair.promotion_status === 'en_cours' ? 'bg-green-100 text-green-800' :
                          pair.promotion_status === 'en_attente' ? 'bg-blue-100 text-blue-800' :
                          pair.promotion_status === 'termine' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {pair.promotion_status === 'en_cours' ? 'En cours' :
                           pair.promotion_status === 'en_attente' ? 'En attente' :
                           pair.promotion_status === 'termine' ? 'Terminé' : 'Annulé'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>{new Date(pair.promotion_start_date).toLocaleDateString('fr-FR')}</div>
                        <div className="text-gray-500">
                          au {new Date(pair.promotion_end_date).toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={editingImpact.value}
                              onChange={(e) => handleImpactChange(e.target.value)}
                              onBlur={() => handleImpactBlur(pair)}
                              onKeyDown={(e) => handleImpactKeyDown(e, pair)}
                              className="w-24 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              min="-100"
                              max="500"
                              step="0.1"
                              autoFocus
                            />
                            {isSaving && (
                              <span className="text-xs text-gray-500">Sauvegarde...</span>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleImpactEdit(pair)}
                            className="text-blue-600 hover:text-blue-800 underline text-sm"
                          >
                            {pair.impact_percentage.toFixed(2)}%
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(pair.predicted_sales_sum)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(pair.additional_sales)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(pair.historical_sales_sum)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlePromotionView;


